import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import FilterOptions from './components/FilterOptions';
import { tripService, Trip } from '../../api/trips';
import { vehicleService, Vehicle } from '../../api/vehicles';
import { employeeService, Employee } from '../../api/Employee';
import {
  formatDatePtBR,
  formatDateTimePtBR,
  formatTimePtBR,
  toISODateTimeUTCFromInput,
} from '../../utils/formatters';
import {
  MapPin,
  Search,
  Plus,
  Car,
  User,
  Calendar,
  Clock,
  FileText,
  Eye,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Loader2,
  AlertCircle,
  CheckCircle2,
  XCircle,
  CheckCircle,
  XOctagon,
  Filter,
} from 'lucide-react';
import {
  PageHeader,
  SectionCard,
  CardHeader,
  CardBody,
  ModernTable,
  Thead,
  Tbody,
  Th,
  Tr,
  Td,
  Input,
  Button,
  ConfirmModal,
  Modal,
  Label,
  Select,
  Textarea,
} from '../../components/ui/SharedLayout';

const ITEMS_PER_PAGE = 20;

const statusLabelMap = {
  pendente: 'Pendente',
  aprovada: 'Aprovada',
  rejeitada: 'Rejeitada',
  em_andamento: 'Em andamento',
  concluida: 'Concluída',
  cancelada: 'Cancelada',
} as const;

const statusClassMap = {
  pendente: 'bg-amber-50 text-amber-800 ring-amber-600/20',
  aprovada: 'bg-emerald-50 text-emerald-800 ring-emerald-600/20',
  rejeitada: 'bg-rose-50 text-rose-800 ring-rose-600/20',
  em_andamento: 'bg-blue-50 text-blue-800 ring-blue-600/20',
  concluida: 'bg-emerald-50 text-emerald-800 ring-emerald-600/20',
  cancelada: 'bg-slate-50 text-slate-700 ring-slate-600/20',
} as const;

interface FilterOption {
  id: string;
  label: string;
}

const SolicitacaoViagem = () => {
  const { user } = useAuth();
  const podeAprovar = user?.role === 'admin' || user?.is_manager;

  const [solicitacoes, setSolicitacoes] = useState<Trip[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const [vehicleId, setVehicleId] = useState<number | null>(null);
  const [employeeId, setEmployeeId] = useState<number | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [destination, setDestination] = useState('');
  const [purpose, setPurpose] = useState('');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [solicitacaoSelecionada, setSolicitacaoSelecionada] = useState<Trip | null>(null);

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filterCategories, setFilterCategories] = useState<{
    [key: string]: FilterOption[];
  }>({});

  const [rejectionReason, setRejectionReason] = useState('');
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [confirmData, setConfirmData] = useState<{
    isOpen: boolean;
    title: string;
    description?: string;
    variant?: 'danger' | 'primary';
    confirmText?: string;
    onConfirm?: () => Promise<void> | void;
  }>({ isOpen: false, title: '' });

  useEffect(() => { if (toast) { const t = setTimeout(() => setToast(null), 2800); return () => clearTimeout(t); } }, [toast]);

  useEffect(() => {
    fetchSolicitacoes();
    fetchVehicles();
    employeeService.getAllEmployees().then(setEmployees);
  }, []);

  const fetchSolicitacoes = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await tripService.getAll();
      setSolicitacoes(res || []);
    } catch (_err) {
      setSolicitacoes([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchVehicles = async () => {
    const res = await vehicleService.getAllVehicles();
    setVehicles(res || []);
  };

  const filteredSolicitacoes = useMemo(
    () => solicitacoes.filter(s => {
      if (s.status === 'concluida') return false;
      if (!searchTerm.trim()) return true;
      const term = searchTerm.trim().toLowerCase();
      return (
        (s.purpose ?? '').toLowerCase().includes(term) ||
        (s.destination ?? '').toLowerCase().includes(term)
      );
    }),
    [solicitacoes, searchTerm],
  );

  const totalPaginas = useMemo(
    () => Math.max(1, Math.ceil(filteredSolicitacoes.length / ITEMS_PER_PAGE)),
    [filteredSolicitacoes.length],
  );

  useEffect(() => {
    if (currentPage > totalPaginas) setCurrentPage(1);
  }, [currentPage, totalPaginas]);

  const { solicitacoesPaginadas, pageRange } = useMemo(() => {
    const total = filteredSolicitacoes.length;
    const safePage = Math.min(currentPage, totalPaginas);
    const startIndex = (safePage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return {
      solicitacoesPaginadas: filteredSolicitacoes.slice(startIndex, endIndex),
      pageRange: {
        start: total === 0 ? 0 : startIndex + 1,
        end: Math.min(endIndex, total),
        total,
      },
    };
  }, [filteredSolicitacoes, currentPage, totalPaginas]);

  const getPageNumbers = useMemo(() => {
    const pages: (number | '...')[] = [];
    const maxVisible = 5;
    if (totalPaginas <= maxVisible + 2) {
      for (let i = 1; i <= totalPaginas; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    const left = Math.max(2, currentPage - 1);
    const right = Math.min(totalPaginas - 1, currentPage + 1);
    if (left > 2) pages.push('...');
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPaginas - 1) pages.push('...');
    pages.push(totalPaginas);
    return pages;
  }, [currentPage, totalPaginas]);

  const handleApplyFilter = (selectedFilters: Record<string, string[]>) => {
    const formattedFilters: { [key: string]: FilterOption[] } = {};
    Object.entries(selectedFilters).forEach(([key, values]) => {
      formattedFilters[key] = values.map(value => ({ id: value, label: value }));
    });
    setFilterCategories(formattedFilters);
  };

  const formatStatus = (status: string | undefined) => {
    if (!status) return '-';
    return statusLabelMap[status as keyof typeof statusLabelMap] || status;
  };

  const getVehicleDetails = (vehicleId: number) =>
    vehicles.find((vehicle) => vehicle.id === vehicleId);

  const getEmployeeDetails = (employeeId: number) =>
    employees.find((employee) => employee.id === employeeId);

  const resetFormFields = () => {
    setVehicleId(null);
    setEmployeeId(null);
    setStartDate('');
    setEndDate('');
    setPurpose('');
    setDestination('');
  };

  const handleCreate = () => {
    resetFormFields();
    setIsModalOpen(true);
  };

  const closeFormModal = () => {
    setIsModalOpen(false);
    resetFormFields();
  };

  const handleEnviarSolicitacao = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      !vehicleId ||
      !employeeId ||
      !startDate ||
      isNaN(Number(vehicleId)) ||
      isNaN(Number(employeeId)) ||
      isNaN(Date.parse(startDate))
    ) {
      setToast({ type: 'error', message: 'Preencha todos os campos obrigatórios corretamente.' });
      return;
    }
    setSubmitting(true);
    try {
      await tripService.create({
        vehicle_id: Number(vehicleId),
        employee_id: Number(employeeId),
        start_date: toISODateTimeUTCFromInput(startDate),
        end_date: endDate ? toISODateTimeUTCFromInput(endDate) : undefined,
        purpose: purpose || undefined,
        destination: destination || undefined,
        status: 'pendente',
      });
      await fetchSolicitacoes();
      closeFormModal();
      setToast({ type: 'success', message: 'Solicitação enviada com sucesso!' });
    } catch (_err) {
      setToast({ type: 'error', message: 'Erro ao enviar solicitação.' });
    } finally {
      setSubmitting(false);
    }
  };

  const visualizarSolicitacao = (solicitacao: Trip) => {
    setSolicitacaoSelecionada(solicitacao);
    setIsViewModalOpen(true);
  };

  const handleAprovar = (trip: Trip) => {
    setConfirmData({
      isOpen: true,
      title: 'Confirmar Aprovação',
      description: `Deseja aprovar a viagem para ${trip.destination || 'destino não informado'}?`,
      confirmText: 'Confirmar Aprovação',
      variant: 'primary',
      onConfirm: async () => {
        setLoading(true);
        try {
          if (trip.status === 'aprovada') {
            setToast({ type: 'error', message: 'Esta viagem já está aprovada.' });
            return;
          }
          if (trip.status !== 'pendente') {
            setToast({ type: 'error', message: `Não é possível aprovar uma viagem com status "${formatStatus(trip.status)}". Apenas viagens pendentes podem ser aprovadas.` });
            return;
          }
          const updatedTrip = await tripService.approve(trip.id);
          await fetchSolicitacoes();
          if (solicitacaoSelecionada?.id === updatedTrip.id) {
            setSolicitacaoSelecionada(updatedTrip);
          }
          setToast({ type: 'success', message: 'Viagem aprovada com sucesso!' });
        } catch (error: any) {
          const msg = error instanceof Error ? error.message : 'Erro ao aprovar a viagem.';
          setToast({ type: 'error', message: msg });
        } finally {
          setLoading(false);
          setConfirmData(p => ({ ...p, isOpen: false }));
        }
      },
    });
  };

  const handleRejeitar = (trip: Trip) => {
    setRejectionReason('');
    setConfirmData({
      isOpen: true,
      title: 'Rejeitar Viagem',
      description: (
        <div className="space-y-3">
          <p>Motivo da rejeição:</p>
          <Textarea
            className="min-h-[100px]"
            placeholder="Informe o motivo da rejeição"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
          />
        </div>
      ) as any,
      variant: 'danger',
      confirmText: 'Confirmar Rejeição',
      onConfirm: async () => {
        if (!rejectionReason) {
          setToast({ type: 'error', message: 'Informe o motivo da rejeição.' });
          return;
        }
        setLoading(true);
        try {
          const updatedTrip = await tripService.reject(trip.id, rejectionReason);
          await fetchSolicitacoes();
          if (solicitacaoSelecionada?.id === trip.id) {
            setSolicitacaoSelecionada(updatedTrip);
          }
          setRejectionReason('');
          setToast({ type: 'success', message: 'Viagem rejeitada com sucesso!' });
        } catch (_err) {
          setToast({ type: 'error', message: 'Erro ao rejeitar a viagem.' });
        } finally {
          setLoading(false);
          setConfirmData(p => ({ ...p, isOpen: false }));
        }
      },
    });
  };

  const formatDate = (dateStr?: string) => formatDatePtBR(dateStr);

  const formatDateTime = (dateStr?: string) => formatDateTimePtBR(dateStr);

  const formatTime = (dateStr?: string) => formatTimePtBR(dateStr);

  return (
    <>
      <PageHeader title="Solicitações de Viagem" hideTitleInBody />

      {toast && (
        <div className={`fixed top-5 right-5 z-[100] inline-flex items-center gap-2.5 rounded-xl px-4 py-3 shadow-[0_10px_30px_-12px_rgba(15,23,42,0.35)] border animate-[fadeIn_0.2s_ease-out] ${toast.type === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-rose-50 border-rose-200 text-rose-800'}`}>
          {toast.type === 'success' ? <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" /> : <XCircle className="h-5 w-5 text-rose-600 shrink-0" />}
          <span className="text-[13px] font-semibold leading-tight">{toast.message}</span>
        </div>
      )}

      <div className="flex flex-col gap-5">
        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-[15px] font-semibold leading-tight text-blue-600">Gestão de viagens</p>
            <h1 className="mt-1 text-[22px] sm:text-[26px] font-bold leading-[1.15] tracking-tight text-slate-900">
              Controle as solicitações de viagem da frota.
              <span className="ml-1.5 text-slate-500 font-semibold text-[18px] sm:text-[20px]">
                Acompanhe o status e aprove ou rejeite pendências.
              </span>
            </h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="primary" size="lg" onClick={handleCreate} disabled={submitting}>
              <Plus className="h-[18px] w-[18px] stroke-[2.1]" />
              Nova solicitação
            </Button>
          </div>
        </div>

        {error && (
          <div className="inline-flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" />
            <div className="flex flex-col gap-0.5">
              <span className="text-[13px] font-bold leading-tight">Não foi possível carregar</span>
              <span className="text-[12.5px] font-medium leading-tight text-rose-700/90">{error}</span>
            </div>
          </div>
        )}

        <SectionCard>
          <CardHeader>
            <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-[15px] font-bold tracking-tight text-slate-900">Solicitações em aberto</h2>
                <p className="mt-0.5 text-[12.5px] font-medium leading-tight text-slate-500">
                  Pendentes, aprovadas, rejeitadas e em andamento
                </p>
              </div>
              <div className="flex items-center gap-2 w-full sm:w-auto sm:flex-1 sm:max-w-lg">
                <div className="relative flex-1 min-w-0">
                  <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    placeholder="Buscar destino, motivo..."
                    className="pl-10 h-10"
                  />
                </div>
                <Button
                  variant="secondary"
                  size="lg"
                  onClick={() => setIsFilterOpen(true)}
                  className="h-10 shrink-0"
                >
                  <Filter className="h-4 w-4 stroke-[1.9]" />
                  Filtros
                </Button>
              </div>
            </div>
          </CardHeader>

          <CardBody>
            {loading && solicitacoes.length === 0 ? (
              <div className="flex items-center justify-center gap-2.5 py-16">
                <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                <span className="text-[13.5px] font-semibold text-slate-600">Carregando solicitações...</span>
              </div>
            ) : (
              <ModernTable className="table-fixed layout-fixed">
                <Thead>
                  <Tr>
                    <Th className="w-[16%] min-w-[200px] max-w-[260px]">Destino</Th>
                    <Th className="w-[12%] min-w-[150px] max-w-[180px]">Data/Hora</Th>
                    <Th className="w-[22%] min-w-[240px] max-w-[380px]">Motivo</Th>
                    <Th className="w-[16%] min-w-[200px] max-w-[260px]">Veículo</Th>
                    <Th className="w-[16%] min-w-[200px] max-w-[260px]">Funcionário</Th>
                    <Th className="w-[10%] min-w-[120px] max-w-[150px]">Status</Th>
                    <Th className="w-[8%] min-w-[90px] max-w-[110px]">Ações</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {solicitacoesPaginadas.length === 0 ? (
                    <Tr>
                      <Td colSpan={7}>
                        <div className="flex flex-col items-center justify-center gap-2 py-14 text-center">
                          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                            <MapPin className="h-7 w-7 stroke-[1.8]" />
                          </div>
                          <div className="text-[14px] font-bold text-slate-700">Nenhuma solicitação de viagem em aberto</div>
                          <p className="max-w-sm text-[12.5px] font-medium leading-relaxed text-slate-500">
                            {searchTerm ? 'Nenhum resultado para a busca atual. Tente outro termo.' : 'Clique em "Nova solicitação" para cadastrar a primeira.'}
                          </p>
                        </div>
                      </Td>
                    </Tr>
                  ) : (
                    solicitacoesPaginadas.map((solicitacao) => {
                      const vehicle = getVehicleDetails(solicitacao.vehicle_id);
                      const employee = getEmployeeDetails(solicitacao.employee_id);
                      const statusLabel = formatStatus(solicitacao.status);
                      const statusClasses = statusClassMap[solicitacao.status as keyof typeof statusClassMap] || '';
                      return (
                        <Tr key={solicitacao.id}>
                          <Td>
                            <div className="min-w-0 w-full max-w-[260px] overflow-hidden">
                              <div className="flex items-start gap-2.5">
                                <div className="mt-0.5 shrink-0 flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-50 text-indigo-700 ring-1 ring-inset ring-indigo-200">
                                  <MapPin className="h-[18px] w-[18px] stroke-[1.9]" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="min-w-0 w-full">
                                    <span className="block truncate text-[13.5px] font-semibold leading-tight text-slate-900" title={solicitacao.destination || '-'}>
                                      {solicitacao.destination || '-'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </Td>
                          <Td>
                            <div>
                              <div className="inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-700 tabular-nums">
                                <Calendar className="h-[15px] w-[15px] text-slate-400" />
                                {formatDate(solicitacao.start_date)}
                              </div>
                              <div className="mt-1 inline-flex items-center gap-1.5 text-[11.5px] font-medium text-slate-500 tabular-nums">
                                <Clock className="h-[13px] w-[13px] text-slate-400" />
                                {formatTime(solicitacao.start_date)}
                              </div>
                            </div>
                          </Td>
                          <Td>
                            <div className="min-w-0 w-full max-w-[360px] overflow-hidden">
                              <span className="block truncate text-[13px] font-medium leading-tight text-slate-800" title={solicitacao.purpose || '-'}>
                                {solicitacao.purpose || '-'}
                              </span>
                            </div>
                          </Td>
                          <Td>
                            <div className="min-w-0 w-full max-w-[260px] overflow-hidden">
                              <div className="flex items-start gap-2.5">
                                <div className="mt-0.5 shrink-0 flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200">
                                  <Car className="h-[18px] w-[18px] stroke-[1.9]" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="min-w-0 w-full">
                                    <span className="block truncate text-[13.5px] font-semibold leading-tight text-slate-900" title={vehicle?.brand || '-'}>
                                      {vehicle?.brand || '-'}
                                    </span>
                                  </div>
                                  <div className="mt-0.5 text-[11.5px] font-medium leading-tight text-slate-500 inline-flex items-center gap-1">
                                    <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-slate-600 font-[11px] font-semibold tabular-nums tracking-wide">
                                      {vehicle?.license_plate || 'Sem placa'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </Td>
                          <Td>
                            <div className="min-w-0 w-full max-w-[260px] overflow-hidden">
                              <div className="flex items-start gap-2.5">
                                <div className="mt-0.5 shrink-0 flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200">
                                  <User className="h-[18px] w-[18px] stroke-[1.9]" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="min-w-0 w-full">
                                    <span className="block truncate text-[13.5px] font-semibold leading-tight text-slate-900" title={employee?.name || '-'}>
                                      {employee?.name || '-'}
                                    </span>
                                  </div>
                                  <div className="mt-0.5 text-[11.5px] font-medium leading-tight text-slate-500">
                                    {employee?.cpf || solicitacao.employee_id}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </Td>
                          <Td>
                            <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11.5px] font-bold ring-1 ring-inset ${statusClasses}`}>
                              {statusLabel}
                            </span>
                          </Td>
                          <Td>
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => visualizarSolicitacao(solicitacao)}
                                title="Visualizar"
                                className="h-8 w-8 rounded-lg text-slate-500 hover:text-blue-700 hover:bg-blue-50 hover:ring-blue-200"
                              >
                                <Eye className="h-4 w-4 stroke-[1.9]" />
                              </Button>
                            </div>
                          </Td>
                        </Tr>
                      );
                    })
                  )}
                </Tbody>
              </ModernTable>
            )}

            {pageRange.total > 0 ? (
              <div className="mt-4 flex flex-col gap-3 border-t border-slate-200/80 pt-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-[12.5px] font-medium leading-tight text-slate-500">
                  Mostrando{' '}
                  <span className="font-bold tabular-nums text-slate-800">{pageRange.start}</span>
                  {'–'}{' '}
                  <span className="font-bold tabular-nums text-slate-800">{pageRange.end}</span>
                  {' de '}
                  <span className="font-bold tabular-nums text-slate-800">{pageRange.total}</span>
                  {' solicitação(ões)'}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                    aria-label="Primeira página"
                  >
                    <ChevronsLeft className="h-4 w-4 stroke-[1.8]" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                    aria-label="Página anterior"
                  >
                    <ChevronLeft className="h-4 w-4 stroke-[1.8]" />
                  </button>
                  <div className="inline-flex items-center gap-1 mx-1">
                    {getPageNumbers.map((n, idx) =>
                      n === '...' ? (
                        <span key={`dots-${idx}`} className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 text-[13px]">…</span>
                      ) : (
                        <button
                          key={n}
                          type="button"
                          onClick={() => setCurrentPage(n)}
                          disabled={submitting}
                          className={`inline-flex h-9 min-w-[36px] cursor-pointer items-center justify-center rounded-lg px-2.5 text-[13px] font-semibold transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1 ${n === currentPage ? 'bg-blue-600 text-white shadow-[0_4px_14px_-4px_rgba(37,99,235,0.55)]' : 'border border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700'}`}
                        >
                          {n}
                        </button>
                      ),
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.min(totalPaginas, p + 1))}
                    disabled={currentPage === totalPaginas}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                    aria-label="Próxima página"
                  >
                    <ChevronRight className="h-4 w-4 stroke-[1.8]" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage(totalPaginas)}
                    disabled={currentPage === totalPaginas}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                    aria-label="Última página"
                  >
                    <ChevronsRight className="h-4 w-4 stroke-[1.8]" />
                  </button>
                </div>
              </div>
            ) : null}
          </CardBody>
        </SectionCard>
      </div>

      {isModalOpen && (
        <Modal
          onClose={closeFormModal}
          maxWidthClass="max-w-2xl"
          title={
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 via-indigo-500 to-indigo-700 text-white shadow-[0_6px_16px_-8px_rgba(37,99,235,0.6)]">
                <MapPin className="h-5 w-5 stroke-[1.9]" />
              </div>
              <div>
                <div className="text-[16px] font-bold tracking-tight text-slate-900 leading-none">
                  Nova Solicitação de Viagem
                </div>
                <div className="mt-1 text-[12px] font-medium text-slate-500 leading-tight">
                  Preencha os campos abaixo para solicitar uma nova viagem
                </div>
              </div>
            </div>
          }
          footer={
            <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:items-center">
              <button
                type="button"
                onClick={closeFormModal}
                disabled={submitting}
                className="inline-flex h-11 items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-5 text-[13px] font-semibold text-slate-700 transition-all hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/30 disabled:pointer-events-none disabled:opacity-60"
              >
                Cancelar
              </button>
              <button
                type="submit"
                form="solicitacao-form"
                disabled={submitting}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-blue-600 via-indigo-600 to-indigo-700 px-6 text-[13px] font-semibold text-white shadow-[0_4px_14px_-4px_rgba(37,99,235,0.55)] transition-all hover:shadow-[0_8px_22px_-6px_rgba(37,99,235,0.65)] focus:outline-none focus:ring-4 focus:ring-blue-500/25 disabled:pointer-events-none disabled:opacity-60 disabled:shadow-none"
              >
                {submitting ? <><Loader2 className="h-4 w-4 animate-spin stroke-[2]" /> Enviando...</> : 'Enviar Solicitação'}
              </button>
            </div>
          }
        >
          <form id="solicitacao-form" onSubmit={handleEnviarSolicitacao} className="space-y-5" noValidate>
            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-blue-500 to-indigo-600" />
                <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-blue-700">Dados da viagem</h3>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50/40 p-4 sm:p-5 space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="flex flex-col gap-1">
                    <Label htmlFor="destino" className="mb-0.5">
                      <span className="inline-flex items-center gap-0.5">
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" /> Destino
                        </span>
                        <span className="text-rose-500 ml-0.5 font-bold leading-none">*</span>
                      </span>
                    </Label>
                    <Input
                      type="text"
                      id="destino"
                      value={destination}
                      onChange={(e) => setDestination(e.target.value)}
                      required
                      placeholder="Ex: Hospital Municipal"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label htmlFor="dataHora" className="mb-0.5">
                      <span className="inline-flex items-center gap-0.5">
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" /> Data e Hora
                        </span>
                        <span className="text-rose-500 ml-0.5 font-bold leading-none">*</span>
                      </span>
                    </Label>
                    <Input
                      type="datetime-local"
                      id="dataHora"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      required
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <Label htmlFor="motivo" className="mb-0.5">
                    <span className="inline-flex items-center gap-0.5">
                      <span className="inline-flex items-center gap-1">
                        <FileText className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" /> Motivo
                      </span>
                      <span className="text-rose-500 ml-0.5 font-bold leading-none">*</span>
                    </span>
                  </Label>
                  <Textarea
                    id="motivo"
                    value={purpose}
                    onChange={(e) => setPurpose(e.target.value)}
                    rows={3}
                    required
                    placeholder="Descreva o motivo da solicitação..."
                  />
                </div>
              </div>
            </div>
            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-emerald-500 to-teal-600" />
                <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-emerald-700">Veículo e Funcionário</h3>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="flex flex-col gap-1">
                    <Label htmlFor="veiculo" className="mb-0.5">
                      <span className="inline-flex items-center gap-0.5">
                        <span className="inline-flex items-center gap-1">
                          <Car className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" /> Veículo
                        </span>
                        <span className="text-rose-500 ml-0.5 font-bold leading-none">*</span>
                      </span>
                    </Label>
                    <Select
                      id="veiculo"
                      value={vehicleId || ''}
                      onChange={(e) => setVehicleId(Number(e.target.value))}
                    >
                      <option value="">Selecione um veículo...</option>
                      {vehicles.map(vehicle => (
                        <option key={vehicle.id} value={vehicle.id}>
                          {vehicle.brand} - {vehicle.license_plate}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label htmlFor="employee" className="mb-0.5">
                      <span className="inline-flex items-center gap-0.5">
                        <span className="inline-flex items-center gap-1">
                          <User className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" /> Funcionário
                        </span>
                        <span className="text-rose-500 ml-0.5 font-bold leading-none">*</span>
                      </span>
                    </Label>
                    <Select
                      id="employee"
                      value={employeeId || ''}
                      onChange={e => setEmployeeId(Number(e.target.value))}
                      required
                    >
                      <option value="">Selecione um funcionário...</option>
                      {employees.map(emp => (
                        <option key={emp.id} value={emp.id}>{emp.name} ({emp.cpf})</option>
                      ))}
                    </Select>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </Modal>
      )}

      {isViewModalOpen && solicitacaoSelecionada && (
        <Modal
          onClose={() => { setIsViewModalOpen(false); setSolicitacaoSelecionada(null); }}
          maxWidthClass="max-w-2xl"
          title={
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 via-indigo-500 to-indigo-700 text-white shadow-[0_6px_16px_-8px_rgba(37,99,235,0.6)]">
                <Eye className="h-5 w-5 stroke-[1.9]" />
              </div>
              <div>
                <div className="text-[16px] font-bold tracking-tight text-slate-900 leading-none">
                  Detalhes da Solicitação
                </div>
                <div className="mt-1 text-[12px] font-medium text-slate-500 leading-tight">
                  Visualize os dados completos da solicitação de viagem
                </div>
              </div>
            </div>
          }
          footer={
            <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:items-center">
              <button
                type="button"
                onClick={() => { setIsViewModalOpen(false); setSolicitacaoSelecionada(null); }}
                className="inline-flex h-11 items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-5 text-[13px] font-semibold text-slate-700 transition-all hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/30"
              >
                Fechar
              </button>
              {podeAprovar && solicitacaoSelecionada?.status === 'pendente' && (
                <>
                  <Button
                    variant="success"
                    onClick={() => { setIsViewModalOpen(false); handleAprovar(solicitacaoSelecionada); }}
                    className="h-11 px-5"
                  >
                    <CheckCircle className="h-4 w-4 stroke-[1.9]" />
                    Aprovar
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => { setIsViewModalOpen(false); handleRejeitar(solicitacaoSelecionada); }}
                    className="h-11 px-5"
                  >
                    <XOctagon className="h-4 w-4 stroke-[1.9]" />
                    Rejeitar
                  </Button>
                </>
              )}
            </div>
          }
        >
          <div className="space-y-5">
            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-blue-500 to-indigo-600" />
                <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-blue-700">Dados da viagem</h3>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50/40 p-4 sm:p-5 space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-slate-500">Destino</span>
                    <p className="text-sm text-slate-900 font-medium">{solicitacaoSelecionada.destination || '-'}</p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-slate-500">Data e Hora</span>
                    <p className="text-sm text-slate-900 font-medium">
                      {formatDateTime(solicitacaoSelecionada.start_date)}
                    </p>
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-semibold text-slate-500">Motivo</span>
                  <p className="text-sm text-slate-900 font-medium whitespace-pre-wrap">{solicitacaoSelecionada.purpose || '-'}</p>
                </div>
              </div>
            </div>
            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-emerald-500 to-teal-600" />
                <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-emerald-700">Veículo e Funcionário</h3>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-slate-500">Veículo</span>
                    <p className="text-sm text-slate-900 font-medium">
                      {vehicles.find(v => v.id === solicitacaoSelecionada.vehicle_id)?.brand || '-'}
                    </p>
                    <span className="text-[11px] font-semibold text-slate-500">
                      Placa: {vehicles.find(v => v.id === solicitacaoSelecionada.vehicle_id)?.license_plate || '-'}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-slate-500">Status</span>
                    <div className="mt-1">
                      <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11.5px] font-bold ring-1 ring-inset ${statusClassMap[solicitacaoSelecionada.status as keyof typeof statusClassMap] || ''}`}>
                        {formatStatus(solicitacaoSelecionada.status)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal>
      )}

      <FilterOptions
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onApplyFilter={handleApplyFilter}
        filterCategories={filterCategories}
      />

      <ConfirmModal
        isOpen={confirmData.isOpen}
        onCancel={() => setConfirmData(p => ({ ...p, isOpen: false }))}
        onConfirm={() => confirmData.onConfirm && confirmData.onConfirm()}
        title={confirmData.title}
        description={confirmData.description || 'Esta ação é permanente.'}
        confirmLabel={confirmData.confirmText || 'Confirmar'}
        confirmVariant={confirmData.variant as any}
        loading={loading}
      />
    </>
  );
};

export default SolicitacaoViagem;
